// src/pages/OptimizedHomePage.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import PageSection from "../components/admin/page-management/PageSection";
import PageSkeletonLoader from "../components/PageSkeletonLoader";
import { useConfig } from "../context/ConfigContext"; // Import the context hook
import LiveEditAdminBar from "../components/LiveEditAdminBar";
import SectionEditOverlay from "../components/SectionEditOverlay";
import { useAuth } from "../context/AuthContext";

const OptimizedHomePage = () => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isAdmin } = useAuth();

  // Get the config data using our context hook
  const { config } = useConfig();

  // Get site name from config
  const siteName = config?.siteName || "My Website";

  useEffect(() => {
    // Using CancelToken to handle potential component unmount during fetch
    const source = axios.CancelToken.source();

    const fetchHomePage = async () => {
      try {
        setLoading(true);

        // Use the specialized home page endpoint for maximum performance
        const response = await axios.get("/api/pages/home", {
          cancelToken: source.token,
        });

        setPage(response.data);
        document.title = `${response.data.title} | ${siteName}`;
        setError(null);
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error("Error fetching home page:", err);
          setError("Error loading home page.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchHomePage();

    // Cleanup function
    return () => {
      source.cancel("Component unmounted");
    };
  }, [siteName]); // Add siteName to the dependency array

  // Helper function for child sections (optimized)
  const findChildSections = (parentId) => {
    if (!page?.sections) return [];

    if (!page._childSectionsMap) {
      // Create a map for faster lookup (only once)
      const map = {};
      page.sections.forEach((section) => {
        if (section.parentId) {
          if (!map[section.parentId.toString()]) {
            map[section.parentId.toString()] = [];
          }
          map[section.parentId.toString()].push(section);
        }
      });

      // Sort child sections by order
      Object.keys(map).forEach((pid) => {
        map[pid].sort((a, b) => a.order - b.order);
      });

      // Cache the map on the page object
      page._childSectionsMap = map;
    }

    return page._childSectionsMap[parentId.toString()] || [];
  };

  // Handle section edit actions from overlay (add before the return statement)
  const handleSectionEdit = async (action, sectionId, direction = null) => {
    if (!isAdmin || !page) return;

    try {
      let response;

      switch (action) {
        case "delete":
          response = await axios.delete(
            `/api/admin/pages/${page._id}/sections/${sectionId}`
          );
          setPage(response.data);
          break;

        case "move":
          // Get the section to move
          const section = page.sections.find((s) => s._id === sectionId);
          if (!section) return;

          // Get all sections at the same level
          const siblingCondition = section.parentId
            ? (s) =>
                s.parentId &&
                s.parentId.toString() === section.parentId.toString()
            : (s) => !s.parentId;

          const sortedSections = [...page.sections]
            .filter(siblingCondition)
            .sort((a, b) => a.order - b.order);

          const sectionIndex = sortedSections.findIndex(
            (s) => s._id === sectionId
          );
          const targetIndex =
            direction === "up" ? sectionIndex - 1 : sectionIndex + 1;

          if (targetIndex >= 0 && targetIndex < sortedSections.length) {
            const sectionIds = sortedSections.map((s) => s._id);
            [sectionIds[sectionIndex], sectionIds[targetIndex]] = [
              sectionIds[targetIndex],
              sectionIds[sectionIndex],
            ];

            response = await axios.post(
              `/api/admin/pages/${page._id}/sections/reorder`,
              {
                sectionIds,
                parentId: section.parentId,
              }
            );
            setPage(response.data);
          }
          break;

        case "duplicate":
          // Get the section to duplicate
          const sectionToDuplicate = page.sections.find(
            (s) => s._id === sectionId
          );
          if (sectionToDuplicate) {
            const duplicatedSection = {
              type: sectionToDuplicate.type,
              content: JSON.parse(JSON.stringify(sectionToDuplicate.content)),
              settings: JSON.parse(JSON.stringify(sectionToDuplicate.settings)),
              parentId: sectionToDuplicate.parentId,
              order: sectionToDuplicate.order + 1,
            };

            response = await axios.post(
              `/api/admin/pages/${page._id}/sections`,
              duplicatedSection
            );
            setPage(response.data);
          }
          break;

        default:
          console.log("Unknown action:", action);
      }
    } catch (err) {
      console.error("Error handling section edit:", err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <PageSkeletonLoader />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <PageSkeletonLoader />
      </div>
    );
  }

  // Determine the container class based on the template (cached calculation)
  const containerClass =
    page.template === "full-width"
      ? "w-full"
      : "container min-h-screen mx-auto px-4";

  // Get top-level sections (cached)
  const topLevelSections =
    page._topLevelSections ||
    (page._topLevelSections = page.sections
      .filter((section) => !section.parentId)
      .sort((a, b) => a.order - b.order));

  return (
    <>
      <Helmet>
        <title>{`${page.metaTitle || page.title} | ${siteName}`}</title>
        <meta
          name="description"
          content={page.metaDescription || page.description}
        />
      </Helmet>

      {/* Live Edit Admin Bar - only shown to admins */}
      {isAdmin && (
        <LiveEditAdminBar page={page} sections={page.sections || []} />
      )}

      <div className={containerClass}>
        {topLevelSections.length > 0 ? (
          topLevelSections.map((section) => {
            // For container sections, pass child sections
            if (section.type === "container") {
              const childSections = findChildSections(section._id);

              return (
                <PageSection
                  key={section._id}
                  section={{
                    ...section,
                    children: childSections,
                  }}
                />
              );
            }

            // For regular sections
            return <PageSection key={section._id} section={section} />;
          })
        ) : (
          <div className="min-h-screen py-12 text-center">
            <h1 className="text-3xl font-bold">{page.title}</h1>
            {page.description && <p className="mt-4">{page.description}</p>}
            <p className="mt-8 text-gray-500">
              This page has no content sections yet.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default OptimizedHomePage;

// // before live editing
// // src/pages/OptimizedHomePage.js
// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import { Helmet } from "react-helmet";
// import PageSection from "../components/admin/page-management/PageSection";
// import PageSkeletonLoader from "../components/PageSkeletonLoader";
// import { useConfig } from "../context/ConfigContext"; // Import the context hook

// const OptimizedHomePage = () => {
//   const [page, setPage] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   // Get the config data using our context hook
//   const { config } = useConfig();

//   // Get site name from config
//   const siteName = config?.siteName || "My Website";

//   useEffect(() => {
//     // Using CancelToken to handle potential component unmount during fetch
//     const source = axios.CancelToken.source();

//     const fetchHomePage = async () => {
//       try {
//         setLoading(true);

//         // Use the specialized home page endpoint for maximum performance
//         const response = await axios.get("/api/pages/home", {
//           cancelToken: source.token,
//         });

//         setPage(response.data);
//         document.title = `${response.data.title} | ${siteName}`;
//         setError(null);
//       } catch (err) {
//         if (!axios.isCancel(err)) {
//           console.error("Error fetching home page:", err);
//           setError("Error loading home page.");
//         }
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchHomePage();

//     // Cleanup function
//     return () => {
//       source.cancel("Component unmounted");
//     };
//   }, [siteName]); // Add siteName to the dependency array

//   // Helper function for child sections (optimized)
//   const findChildSections = (parentId) => {
//     if (!page?.sections) return [];

//     if (!page._childSectionsMap) {
//       // Create a map for faster lookup (only once)
//       const map = {};
//       page.sections.forEach((section) => {
//         if (section.parentId) {
//           if (!map[section.parentId.toString()]) {
//             map[section.parentId.toString()] = [];
//           }
//           map[section.parentId.toString()].push(section);
//         }
//       });

//       // Sort child sections by order
//       Object.keys(map).forEach((pid) => {
//         map[pid].sort((a, b) => a.order - b.order);
//       });

//       // Cache the map on the page object
//       page._childSectionsMap = map;
//     }

//     return page._childSectionsMap[parentId.toString()] || [];
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center min-h-screen">
//         <PageSkeletonLoader />
//       </div>
//     );
//   }

//   if (error || !page) {
//     return (
//       <div className="flex justify-center items-center min-h-screen">
//         <PageSkeletonLoader />
//       </div>
//     );
//   }

//   // Determine the container class based on the template (cached calculation)
//   const containerClass =
//     page.template === "full-width"
//       ? "w-full"
//       : "container min-h-screen mx-auto px-4";

//   // Get top-level sections (cached)
//   const topLevelSections =
//     page._topLevelSections ||
//     (page._topLevelSections = page.sections
//       .filter((section) => !section.parentId)
//       .sort((a, b) => a.order - b.order));

//   return (
//     <>
//       <Helmet>
//         <title>{`${page.metaTitle || page.title} | ${siteName}`}</title>
//         <meta
//           name="description"
//           content={page.metaDescription || page.description}
//         />
//       </Helmet>

//       <div className={containerClass}>
//         {topLevelSections.length > 0 ? (
//           topLevelSections.map((section) => {
//             // For container sections, pass child sections
//             if (section.type === "container") {
//               const childSections = findChildSections(section._id);

//               return (
//                 <PageSection
//                   key={section._id}
//                   section={{
//                     ...section,
//                     children: childSections,
//                   }}
//                 />
//               );
//             }

//             // For regular sections
//             return <PageSection key={section._id} section={section} />;
//           })
//         ) : (
//           <div className="min-h-screen py-12 text-center">
//             <h1 className="text-3xl font-bold">{page.title}</h1>
//             {page.description && <p className="mt-4">{page.description}</p>}
//             <p className="mt-8 text-gray-500">
//               This page has no content sections yet.
//             </p>
//           </div>
//         )}
//       </div>
//     </>
//   );
// };

// export default OptimizedHomePage;
